import React, { useState, useRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import TetherComponent from 'react-tether'
import { FaIcon } from '../components'
import { GGButton } from './GGButton'

// React twin of the DS "Dropdown · Menu" context menu (preview/comp-menu.html).
// The spec defines the panel and items only; the trigger defaults to an
// icon-only ghost button and can be overridden via triggerIcon/triggerLabel.
// The panel renders through react-tether (same as SimDropdown) so it escapes
// overflow-clipping scroll containers like the dashboard table wrappers.

// 'md' has no Font Awesome class (nothing between 1em and fa-lg) — it is
// sized purely by the gg-menu__item-icon--md wrapper's font-size.
const FA_SIZE_CLASSES = { lg: 'fa-lg', xl: 'fa-xl' }

// 'together' lets tether flip the panel above the trigger when it would
// overflow the window bottom (e.g. on the last table rows); without it the
// window constraint never changes the attachment side (RTM-9649)
const TETHER_CONSTRAINTS = [ { to: 'window', attachment: 'together', pin: true } ]

export const GGMenu = ({
  options,
  onSelect,
  triggerIcon = 'ellipsis-vertical',
  triggerLabel,
  className,
  position = 'right',
  iconSize,
}) => {
  const [ open, setOpen ] = useState(false)
  const triggerRef = useRef(null)
  const panelRef = useRef(null)

  useEffect(() => {
    if (!open) { return }
    const handleClickOutside = (e) => {
      if (triggerRef.current?.contains(e.target) || panelRef.current?.contains(e.target)) { return }
      setOpen(false)
    }
    const handleScroll = () => setOpen(false)
    const handleKey = (e) => {
      if (e.key === 'Escape') {
        setOpen(false)
      }
    }
    document.addEventListener('click', handleClickOutside)
    document.addEventListener('keydown', handleKey)
    window.addEventListener('scroll', handleScroll, true)
    return () => {
      document.removeEventListener('click', handleClickOutside)
      document.removeEventListener('keydown', handleKey)
      window.removeEventListener('scroll', handleScroll, true)
    }
  }, [ open ])

  const handleSelect = (opt) => {
    setOpen(false)
    if (onSelect) {
      onSelect(opt.value)
    }
  }

  const otherPosition = position === 'left' ? 'right' : 'left'

  return (
    <div className={[ 'gg-menu', className ].filter(Boolean).join(' ')}>
      <TetherComponent
        attachment={`top ${otherPosition}`}
        targetAttachment={`bottom ${position}`}
        offset='4px 0'
        constraints={ TETHER_CONSTRAINTS }
      >
        <div className='gg-menu__trigger' ref={triggerRef}>
          <GGButton
            variant='ghost'
            icon={triggerIcon}
            label={triggerLabel}
            ariaLabel={triggerLabel || window.I18n.t('buttons.more_options')}
            onClick={() => setOpen(!open)}
            size='sm'
          />
        </div>
        {open && (
          <div className='gg-menu__panel' ref={panelRef}>
            {options.map((opt, index) => (
              opt.separator ? (
                <div key={`separator-${index}`} className='gg-menu__separator'/>
              ) : (
                <div
                  key={opt.value}
                  className={[
                    'gg-menu__item',
                    opt.active ? 'gg-menu__item--active' : '',
                    opt.danger ? 'gg-menu__item--danger' : '',
                  ].filter(Boolean).join(' ')}
                  onClick={() => handleSelect(opt)}
                >
                  {opt.icon && (
                    <span
                      className={[
                        'gg-menu__item-icon',
                        iconSize ? `gg-menu__item-icon--${iconSize}` : '',
                      ].filter(Boolean).join(' ')}
                    >
                      <FaIcon icon={opt.icon} usePro={opt.usePro} className={FA_SIZE_CLASSES[iconSize]}/>
                    </span>
                  )}
                  {opt.label}
                </div>
              )
            ))}
          </div>
        )}
      </TetherComponent>
    </div>
  )
}

GGMenu.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.string,
      icon: PropTypes.string,
      usePro: PropTypes.bool,
      active: PropTypes.bool,
      danger: PropTypes.bool,
      separator: PropTypes.bool,
    }),
  ).isRequired,
  onSelect: PropTypes.func.isRequired,
  triggerIcon: PropTypes.string,
  triggerLabel: PropTypes.string,
  className: PropTypes.string,
  position: PropTypes.oneOf([ 'left', 'right' ]),
  iconSize: PropTypes.oneOf([ 'md', 'lg', 'xl' ]),
}
